import React, { useState } from 'react';
import './BookingSelectionPanel.css';
import {
    BOOKING_DEPOSIT_AMOUNT,
    formatBookingDeposit,
    bookingDepositFormatter,
} from '../../constants/bookingDeposit';
import { BOOKING_STEPS } from '../../constants/bookingSteps';
import { Extra, Modality, Room, Turn } from '../../models/types';
import {
    calculateExtraTotal,
    formatExtraPriceDisplay,
    formatExtraUnitsSuffix,
    sortExtrasByCatalogOrder,
} from '../../utils/extraPricing';
import { resolveExtraIconKind } from '../../utils/extraIcons';
import { getRoomImageUrl } from '../../utils/roomImages';
import {
    formatIncludedExtraDisplay,
    formatMonitorBonusLine,
    getVillalbaStandaloneMonitorLine,
} from '../../utils/villalbaPromotions';
import {
    filterOutMeriendaPerritoExtra,
    formatMeriendaUpgradePriceLabel,
    hasMeriendaTypeChoice,
    isMeriendaExtra,
    isMeriendaPerritoExtra,
} from '../../utils/meriendaUpgrade';
import { formatExtraDisplayName } from '../../utils/extraDisplayName';
import { ExtraIcon } from '../ExtraItem/ExtraIcons';

interface BookingSelectionPanelProps {
    isOpen: boolean;
    onClose: () => void;
    currentStep: number;
    shopId: number;
    shopName: string;
    room: Room | null;
    selectedDate: Date | null;
    selectedTurn: Turn | null;
    selectedModality: Modality | null;
    selectedExtras: Extra[];
    availableExtras?: Extra[];
    onRemoveExtra?: (extraId: number) => void;
    dismissible?: boolean;
}

const currencyFormatter = new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const formatModalityPrice = (price: number | null) => {
    if (price === null) {
        return 'Consultar precio';
    }
    return currencyFormatter.format(price);
};

const capitalize = (value: string) =>
    value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const LocationIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path d="M12 21s7-5.2 7-11a7 7 0 1 0-14 0c0 5.8 7 11 7 11z" />
        <circle cx="12" cy="10" r="2.4" />
    </svg>
);

const RoomIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path d="M4 20V8.5L12 4l8 4.5V20" />
        <path d="M9.5 20v-6h5v6" />
    </svg>
);

const CalendarIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
        <path d="M8 3.5v3M16 3.5v3M3.5 10h17" />
    </svg>
);

const ClockIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <circle cx="12" cy="12" r="8.2" />
        <path d="M12 8v4.4l2.8 1.8" />
    </svg>
);

const ShieldIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path d="M12 3l7 3v5.5c0 4.4-2.9 7.8-7 9.5-4.1-1.7-7-5.1-7-9.5V6l7-3z" />
        <path d="M9.2 12.2l1.9 1.9 3.8-4" />
    </svg>
);

const LockSmallIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <rect x="5" y="11" width="14" height="10" rx="2" />
        <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
);

const RoomThumbnail: React.FC<{
    shopId: number;
    shopName: string;
    room: Room;
}> = ({ shopId, shopName, room }) => {
    const imageUrl = getRoomImageUrl(shopId, room.room_num, shopName);
    const fallbackUrl = `https://via.placeholder.com/160x120/016BB8/ffffff?text=${encodeURIComponent(room.room_name)}`;
    const [src, setSrc] = useState(imageUrl ?? fallbackUrl);

    return (
        <img
            src={src}
            alt={room.room_name}
            className="booking-selection-thumb"
            onError={() => {
                if (src !== fallbackUrl) {
                    setSrc(fallbackUrl);
                }
            }}
        />
    );
};

const BookingSelectionPanel: React.FC<BookingSelectionPanelProps> = ({
    isOpen,
    onClose,
    currentStep,
    shopId,
    shopName,
    room,
    selectedDate,
    selectedTurn,
    selectedModality,
    selectedExtras,
    availableExtras = [],
    onRemoveExtra,
    dismissible = true,
}) => {
    if (!isOpen) {
        return null;
    }

    const formattedDate = selectedDate
        ? capitalize(
            selectedDate.toLocaleDateString('es-ES', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
            })
        )
        : null;

    const showDateTurn = Boolean(selectedDate && selectedTurn && currentStep >= BOOKING_STEPS.MODALITY);
    const turnLabel =
        selectedTurn && showDateTurn
            ? `${selectedTurn.turn_short_name} (${selectedTurn.time_start} - ${selectedTurn.time_end})`
            : null;

    const includedExtras = selectedModality?.includedExtras ?? [];
    const includedExtraIds = new Set(includedExtras.map((extra) => Number(extra.extra_id)));
    const orderedSelectedExtras = sortExtrasByCatalogOrder(selectedExtras, availableExtras);
    const billableSelectedExtras = orderedSelectedExtras.filter(
        (extra) => !includedExtraIds.has(Number(extra.extra_id))
    );
    const showMeriendaTypeChoice = hasMeriendaTypeChoice(includedExtras, availableExtras);
    const meriendaPerritoSelected = billableSelectedExtras.find(isMeriendaPerritoExtra);
    const displayOptionalExtras = showMeriendaTypeChoice
        ? filterOutMeriendaPerritoExtra(billableSelectedExtras)
        : billableSelectedExtras;
    const standaloneMonitorLine = getVillalbaStandaloneMonitorLine(
        shopId,
        billableSelectedExtras,
        includedExtras,
        shopName
    );
    const modalityPrice = selectedModality?.price ?? 0;
    const extrasTotal = billableSelectedExtras.reduce(
        (total, extra) => total + calculateExtraTotal(extra),
        0
    );
    const totalAmount = Number(modalityPrice) + Number(extrasTotal);
    const extrasCount = displayOptionalExtras.length;
    const hasIncludedSection = includedExtras.length > 0 || Boolean(standaloneMonitorLine);
    const hasOptionalExtrasSection = extrasCount > 0 || extrasTotal > 0;
    const isSummaryStep = currentStep === BOOKING_STEPS.SUMMARY;
    const isPaymentStep = currentStep === BOOKING_STEPS.PERSONAL_DATA;
    // Same Resumen chrome on mobile review (step 6) and payment (step 7)
    const showPaymentBreakdown = isSummaryStep || isPaymentStep;
    const remainingAmount = Math.max(0, totalAmount - BOOKING_DEPOSIT_AMOUNT);

    return (
        <div className="booking-selection-spacer">
            <div
                className={`booking-selection-overlay${dismissible ? '' : ' booking-selection-overlay--locked'}`}
                onClick={() => {
                    if (
                        dismissible &&
                        window.matchMedia('(max-width: 767px)').matches
                    ) {
                        onClose();
                    }
                }}
            >
                <aside
                    id="booking-selection-panel"
                    className={`booking-selection-panel${showPaymentBreakdown ? ' booking-selection-panel--payment' : ''}`}
                    onClick={(event) => event.stopPropagation()}
                    role="dialog"
                    aria-modal={dismissible ? true : undefined}
                    aria-labelledby="booking-selection-title"
                >
                <div className="booking-selection-handle" aria-hidden="true" />

                <div className="booking-selection-panel-header">
                    <h3 id="booking-selection-title">Resumen de tu reserva</h3>
                    {dismissible ? (
                        <button
                            type="button"
                            className="booking-selection-close"
                            onClick={onClose}
                            aria-label="Cerrar"
                        >
                            ×
                        </button>
                    ) : null}
                </div>

                <div className="booking-selection-body">
                    <div className="booking-selection-venue">
                        {room && (
                            <RoomThumbnail shopId={shopId} shopName={shopName} room={room} />
                        )}
                        <ul className="booking-selection-venue-list">
                            <li>
                                <span className="booking-selection-venue-icon" aria-hidden="true">
                                    <LocationIcon />
                                </span>
                                <span>{shopName}</span>
                            </li>
                            {room && (
                                <li>
                                    <span className="booking-selection-venue-icon" aria-hidden="true">
                                        <RoomIcon />
                                    </span>
                                    <span>{room.room_name}</span>
                                </li>
                            )}
                            {formattedDate && showDateTurn && (
                                <li>
                                    <span className="booking-selection-venue-icon" aria-hidden="true">
                                        <CalendarIcon />
                                    </span>
                                    <span>{formattedDate}</span>
                                </li>
                            )}
                            {turnLabel && (
                                <li>
                                    <span className="booking-selection-venue-icon" aria-hidden="true">
                                        <ClockIcon />
                                    </span>
                                    <span>{turnLabel}</span>
                                </li>
                            )}
                        </ul>
                    </div>

                    {selectedModality && (
                        <>
                            <section className="booking-selection-modality" aria-label="Modalidad">
                                <h4 className="booking-selection-section-title">Modalidad</h4>
                                <div className="booking-selection-modality-row">
                                    <span>{selectedModality.label}</span>
                                    <span className="booking-selection-modality-price">
                                        {formatModalityPrice(selectedModality.price)}
                                    </span>
                                </div>
                            </section>

                            {hasIncludedSection && (
                                <section className="booking-selection-included" aria-label="Incluidos">
                                    <h4 className="booking-selection-section-title booking-selection-section-title--included">
                                        Incluidos
                                    </h4>
                                    <ul className="booking-selection-extras-list">
                                        {includedExtras.map((extra) => {
                                            const isMeriendaUpgrade =
                                                showMeriendaTypeChoice &&
                                                isMeriendaExtra(extra) &&
                                                Boolean(meriendaPerritoSelected);
                                            const upgradePriceLabel =
                                                isMeriendaUpgrade && meriendaPerritoSelected
                                                    ? formatMeriendaUpgradePriceLabel(
                                                          meriendaPerritoSelected,
                                                          currencyFormatter
                                                      )
                                                    : '';

                                            return (
                                                <li
                                                    key={`included-${extra.extra_id}`}
                                                    className="booking-selection-extra-row booking-selection-extra-row--included"
                                                >
                                                    <span
                                                        className="booking-selection-extra-icon booking-selection-extra-icon--included"
                                                        aria-hidden="true"
                                                    >
                                                        <ExtraIcon
                                                            kind={resolveExtraIconKind(extra.extra_name)}
                                                        />
                                                    </span>
                                                    <span className="booking-selection-extra-name">
                                                        {formatIncludedExtraDisplay(
                                                            extra,
                                                            shopId,
                                                            billableSelectedExtras,
                                                            shopName
                                                        )}
                                                    </span>
                                                    {upgradePriceLabel ? (
                                                        <span className="booking-selection-extra-price booking-selection-extra-price--upgrade">
                                                            {upgradePriceLabel}
                                                        </span>
                                                    ) : (
                                                        <span className="booking-selection-included-badge">
                                                            Incluido
                                                        </span>
                                                    )}
                                                    <span className="booking-selection-extra-spacer" aria-hidden="true" />
                                                </li>
                                            );
                                        })}

                                        {standaloneMonitorLine && (
                                            <li
                                                key={`promo-${standaloneMonitorLine.extra_id}`}
                                                className="booking-selection-extra-row booking-selection-extra-row--included"
                                            >
                                                <span
                                                    className="booking-selection-extra-icon booking-selection-extra-icon--included"
                                                    aria-hidden="true"
                                                >
                                                    <ExtraIcon kind="monitor" />
                                                </span>
                                                <span className="booking-selection-extra-name">
                                                    {formatMonitorBonusLine(standaloneMonitorLine)}
                                                </span>
                                                <span className="booking-selection-included-badge">
                                                    Incluido
                                                </span>
                                                <span className="booking-selection-extra-spacer" aria-hidden="true" />
                                            </li>
                                        )}
                                    </ul>
                                </section>
                            )}

                            {hasOptionalExtrasSection && (
                                <section className="booking-selection-extras" aria-label="Extras añadidos">
                                    <h4 className="booking-selection-section-title booking-selection-section-title--extras">
                                        Extras añadidos ({extrasCount})
                                    </h4>
                                    <ul className="booking-selection-extras-list">
                                        {displayOptionalExtras.map((extra) => (
                                            <li key={extra.extra_id} className="booking-selection-extra-row">
                                                <span className="booking-selection-extra-icon" aria-hidden="true">
                                                    <ExtraIcon kind={resolveExtraIconKind(extra.extra_name)} />
                                                </span>
                                                <span className="booking-selection-extra-name">
                                                    {formatExtraDisplayName(extra)}
                                                    {formatExtraUnitsSuffix(extra)}
                                                </span>
                                                <span className="booking-selection-extra-price">
                                                    {formatExtraPriceDisplay(extra, currencyFormatter)}
                                                </span>
                                                {onRemoveExtra ? (
                                                    <button
                                                        type="button"
                                                        className="booking-selection-extra-remove"
                                                        onClick={() => onRemoveExtra(extra.extra_id)}
                                                        aria-label={`Quitar ${formatExtraDisplayName(extra)}`}
                                                    >
                                                        ×
                                                    </button>
                                                ) : (
                                                    <span className="booking-selection-extra-spacer" aria-hidden="true" />
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="booking-selection-extras-total">
                                        <span>Total extras</span>
                                        <span>{currencyFormatter.format(extrasTotal)}</span>
                                    </div>
                                </section>
                            )}
                        </>
                    )}
                </div>

                {selectedModality && (
                    <div className={`booking-selection-footer${isSummaryStep ? ' booking-selection-footer--summary' : ''}`}>
                        {!isSummaryStep && (
                            <div
                                className={`booking-selection-grand-total${showPaymentBreakdown ? ' booking-selection-grand-total--boxed' : ''}`}
                            >
                                <span className="booking-selection-grand-label">TOTAL</span>
                                <span className="booking-selection-grand-value">
                                    {currencyFormatter.format(totalAmount)}
                                </span>
                            </div>
                        )}

                        {showPaymentBreakdown ? (
                            <>
                                <div className="booking-selection-payment-split" aria-label="Desglose del pago">
                                    <div className="booking-selection-payment-col booking-selection-payment-col--today">
                                        <span className="booking-selection-payment-label">Hoy pagarás</span>
                                        <span className="booking-selection-payment-amount">
                                            {formatBookingDeposit()}
                                        </span>
                                        <span className="booking-selection-payment-hint">Reserva del local</span>
                                    </div>
                                    <div className="booking-selection-payment-col">
                                        <span className="booking-selection-payment-label">
                                            El día de la celebración
                                        </span>
                                        <span className="booking-selection-payment-amount">
                                            {bookingDepositFormatter.format(remainingAmount)}
                                        </span>
                                        <span className="booking-selection-payment-hint">En el centro</span>
                                    </div>
                                </div>

                                <div className="booking-selection-secure-footer">
                                    <span className="booking-selection-secure-footer-icon" aria-hidden="true">
                                        <LockSmallIcon />
                                    </span>
                                    <p>Tus datos están protegidos. Reserva 100% segura.</p>
                                </div>
                            </>
                        ) : (
                            <div className="booking-selection-info">
                                <span className="booking-selection-info-icon" aria-hidden="true">
                                    <ShieldIcon />
                                </span>
                                <p>Podrás revisar y modificar cualquier detalle antes del pago.</p>
                            </div>
                        )}
                    </div>
                )}
            </aside>
            </div>
        </div>
    );
};

export default BookingSelectionPanel;
